package music.jammers.client.shared.event.domain.validator

import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import music.jammers.client.shared.event.domain.model.ValidationError
import music.jammers.client.shared.event.domain.model.ValidationErrorValue
import music.jammers.client.shared.event.domain.usecase.ValidateEventUseCase
import music.jammers.client.shared.event.domain.usecase.ValidateEventUseCase.Fields.APPLIES_LIMIT
import music.jammers.client.shared.event.domain.usecase.ValidateEventUseCase.Fields.SONG_LIMIT
import music.jammers.client.shared.event.domain.usecase.ValidateEventUseCase.Fields.TEXT_LENGTH_LIMIT
import music.jammers.shared.event.domain.model.EventInstrument

class EventFieldValidator {

    fun validate(name: String, value: String): ValidationError? {
        val error = when (name) {
            ValidateEventUseCase.TITLE              -> {
                validateString(name, value, TITLE_LENGTH_LIMIT)
            }

            ValidateEventUseCase.DESCRIPTION        -> {
                validateString(name, value, DESCRIPTION_LENGTH_LIMIT)
            }

            ValidateEventUseCase.GENRE              -> {
                validateString(name, value, TEXT_LENGTH_LIMIT)
            }

            ValidateEventUseCase.SPOT               -> {
                validateString(name, value, TEXT_LENGTH_LIMIT)
            }

            ValidateEventUseCase.SONGS_COUNT        -> {
                validateInt(name, value, SONG_LIMIT)
            }

            ValidateEventUseCase.APPLIES_PER_PERSON -> {
                validateInt(name, value, APPLIES_LIMIT)
            }

            ValidateEventUseCase.DATE               -> {
                validateDate(name, value)
            }

            else                                    -> null
        }

        return error
    }

    fun validate(instrumentList: List<EventInstrument>): ValidationError? =
        when {
            instrumentList.isEmpty()                       -> {
                ValidationError(
                    ValidateEventUseCase.SELECTED_INSTRUMENTS,
                    ValidationErrorValue.ItemNotSelected
                )
            }

            instrumentList.size > MAX_SELECTED_INSTRUMENTS -> {
                ValidationError(
                    ValidateEventUseCase.SELECTED_INSTRUMENTS,
                    ValidationErrorValue.ItemsMaxCount(MAX_SELECTED_INSTRUMENTS, instrumentList.size)
                )
            }

            else                                           -> null
        }

    private fun validateString(name: String, value: String, maxLength: Int): ValidationError? = when {
        value.isEmpty()                         -> {
            ValidationError(name, ValidationErrorValue.EmptyValue)
        }

        !allowedTextSymbolsRegex.matches(value) -> {
            ValidationError(name, ValidationErrorValue.InvalidSymbol)
        }

        value.length > maxLength                -> {
            ValidationError(name, ValidationErrorValue.StringMaxLength(maxLength, value.length))
        }

        else                                    -> {
            null
        }
    }

    private fun validateInt(name: String, value: String, max: Int): ValidationError? {
        val currentValue = value.ifEmpty {
            "0"
        }

        if (!digitRegex.matches(currentValue)) {
            return ValidationError(name, ValidationErrorValue.InvalidDigit)
        }

        val intValue = currentValue.toIntOrNull() ?: 0

        return if (intValue > max) {
            ValidationError(name, ValidationErrorValue.MaxInt(max))
        } else if (intValue < 1) {
            ValidationError(name, ValidationErrorValue.MinInt(1))
        } else {
            null
        }
    }

    private fun validateDate(fieldName: String, value: String): ValidationError? {
        try {
            val dateValue = LocalDateTime.parse(value)
            val current = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())

            return if (dateValue > current) {
                null
            } else {
                ValidationError(fieldName, ValidationErrorValue.DateBeforeNow)
            }
        } catch (ignore: Throwable) {
            return ValidationError(fieldName, ValidationErrorValue.InvalidDate)
        }
    }

    internal companion object {

        const val TITLE_LENGTH_LIMIT = 30
        const val DESCRIPTION_LENGTH_LIMIT = 500
        const val MAX_SELECTED_INSTRUMENTS = APPLIES_LIMIT

        private val allowedTextSymbolsRegex = """[A-zА-я0-9." -]+${'$'}""".toRegex()
        private val digitRegex = "^\\d+$".toRegex()
    }
}